import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../css/ReporteIngresos.css';
import { useUser } from '../lib/UserContext';

function ReporteIngresos() {
  const [ingresos, setIngresos] = useState([]);
  const [filteredIngresos, setFilteredIngresos] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [totalSum, setTotalSum] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Obtener el usuario actual y verificar si es administrador
  const { isAdmin } = useUser();
  const canEditValues = isAdmin();

  const apiUrl = process.env.REACT_APP_API_URL;

  // 🔍 DEBUG: Logs para depuración
  useEffect(() => {
    console.log('=== DEBUG REPORTE INGRESOS - INFORMACIÓN DE USUARIO ===');
    console.log('isAdmin():', isAdmin());
    console.log('canEditValues:', canEditValues);
    console.log('=======================================================');
  }, [isAdmin, canEditValues]);

  useEffect(() => {
    axios.get(`${apiUrl}/Donantes/Donaciones`)
      .then((response) => {
        setIngresos(response.data);
        setFilteredIngresos(response.data);
        calculateTotalSum(response.data); // Calcular sumatoria inicial
        console.info(response.data);
      })
      .catch((error) => {
        console.error('Error al obtener los ingresos:', error);
      });
  }, [apiUrl]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const normalizeDate = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const normalizeDateEnd = (date) => {
    const normalized = new Date(date);
    normalized.setHours(23, 59, 59, 0);
    return normalized;
  };

  const handleFilterClick = () => {
    let filtered = ingresos;

    if (search) {
      filtered = filtered.filter((ingreso) =>
        String(ingreso.identificacion).includes(search)
      );
    }

    if (startDate && endDate) {
      const normalizedStart = normalizeDate(startDate);
      const normalizedEnd = normalizeDateEnd(endDate);

      filtered = filtered.filter((ingreso) => {
        const fechaIngreso = normalizeDate(ingreso.fechaDonacion);
        return fechaIngreso >= normalizedStart && fechaIngreso <= normalizedEnd;
      });
    }

    setFilteredIngresos(filtered);
    calculateTotalSum(filtered); // Calcular sumatoria después de filtrar
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedIngresos = [...filteredIngresos].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredIngresos(sortedIngresos);
    setSortConfig({ key, direction });
  };

  const calculateTotalSum = (data) => {
    const sum = data.reduce((acc, ingreso) => acc + ingreso.valor, 0);
    setTotalSum(sum); // Actualizar el estado con la sumatoria
  };

  const formatCurrency = (value) => {
    return Number(value).toLocaleString('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleGoBack = () => {
    window.location.href = '/opciones';
  };

  const handleEditClick = (ingreso) => {
    if (!canEditValues) {
      toast.error('Solo el administrador puede editar valores');
      return;
    }
    console.log('📝 Registro seleccionado para editar:', ingreso);
    console.log('📝 Propiedades del registro:', Object.keys(ingreso));
    setEditingRecord(ingreso);
    setEditValue(ingreso.valor);
    setShowModal(true);
  };

  const handleCancelEdit = () => {
    setShowModal(false);
    setEditingRecord(null);
    setEditValue('');
  };

  const handleSaveEdit = async () => {
    console.log('=== GUARDAR EDICIÓN ===');
    console.log('Registro a editar:', editingRecord);
    console.log('editValue:', editValue);
    
    if (!canEditValues) {
      toast.error('Solo el administrador puede editar valores');
      return;
    }

    if (!editingRecord) {
      toast.error('No hay registro seleccionado');
      return;
    }

    try {
      const newValue = parseFloat(editValue);
      console.log('newValue parseado:', newValue);
      
      if (isNaN(newValue) || newValue <= 0) {
        toast.error('El valor debe ser un número positivo');
        return;
      }

      const url = `${apiUrl}/Donantes/Donacion/${editingRecord.id}`;
      const payload = {
        id: editingRecord.id,
        id_Donante: editingRecord.id_Donante,
        fecha_Ingreso: editingRecord.fechaDonacion,
        fecha_Registro: editingRecord.fecha_Registro,
        id_Concepto: editingRecord.id_Concepto,
        valor: newValue,
        id_Registrante: editingRecord.id_Registrante
      };
      
      console.log('URL completa:', url);
      console.log('Payload completo:', payload);
      
      // Actualizar en el backend
      console.log('Enviando PUT...');
      const response = await axios.put(url, payload);
      
      console.log('✅ Respuesta exitosa del backend:', response);
      console.log('✅ response.data:', response.data);
      console.log('✅ response.status:', response.status);

      // Actualizar en el estado local
      const updatedIngresos = ingresos.map(ing => 
        ing.id === editingRecord.id ? { ...ing, valor: newValue } : ing
      );
      
      console.log('✅ ingresos actualizados');
      setIngresos(updatedIngresos);
      
      // Actualizar los filtrados también
      const updatedFiltered = filteredIngresos.map(ing =>
        ing.id === editingRecord.id ? { ...ing, valor: newValue } : ing
      );
      
      console.log('✅ filteredIngresos actualizados');
      setFilteredIngresos(updatedFiltered);
      calculateTotalSum(updatedFiltered);
      
      setShowModal(false);
      setEditingRecord(null);
      setEditValue('');
      toast.success('Valor actualizado exitosamente');
      console.log('======================');
    } catch (error) {
      console.error('❌ ERROR COMPLETO:', error);
      console.error('❌ error.message:', error.message);
      console.error('❌ error.response:', error.response);
      console.error('❌ error.response.status:', error.response?.status);
      console.error('❌ error.response.data:', error.response?.data);
      console.error('❌ error.response.statusText:', error.response?.statusText);
      console.error('❌ error.config:', error.config);
      console.error('❌ error.config.url:', error.config?.url);
      console.error('❌ error.config.method:', error.config?.method);
      console.error('❌ error.config.data:', error.config?.data);
      
      let errorMsg = 'Error desconocido';
      
      if (error.response) {
        // El servidor respondió con un código de error
        const status = error.response.status;
        const data = error.response.data;
        
        if (typeof data === 'string') {
          errorMsg = `${status}: ${data}`;
        } else if (data && data.message) {
          errorMsg = `${status}: ${data.message}`;
        } else if (data && data.title) {
          errorMsg = `${status}: ${data.title}`;
        } else {
          errorMsg = `Error HTTP ${status}`;
        }
      } else if (error.request) {
        // La petición se hizo pero no hubo respuesta
        errorMsg = 'No se recibió respuesta del servidor';
      } else {
        // Algo pasó al configurar la petición
        errorMsg = error.message;
      }
      
      console.error('❌ Mensaje de error final:', errorMsg);
      toast.error(`Error al actualizar: ${errorMsg}`);
    }
  };

  return (
    <div className="reporte-container">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="reporte-content">
        <div className="table-wrapper">
          <div className="search-container">
            <h1 className="reporte-ingresos-title">Reporte de Ingresos</h1>

            <label htmlFor="search">Filtrar por Identificación:</label>
            <input
              type="text"
              id="search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Ingrese identificación"
            />

            <div className="date-filters">
              <label htmlFor="startDate">Fecha Inicio:</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={handleStartDateChange}
              />

              <label htmlFor="endDate">Fecha Fin:</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={handleEndDateChange}
              />
            </div>

            <button onClick={handleFilterClick}>Aplicar Filtro</button>
          </div>

           {/* Mostrar sumatoria total */}
           <div className="total-sum">
              <h3>Total: {formatCurrency(totalSum)}</h3>
            </div>


          <div className="table-responsive">
            <table className="ingresos-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('identificacion')}>Identificación</th>
                  <th onClick={() => handleSort('nombres')}>Nombres</th>
                  <th onClick={() => handleSort('apellidos')}>Apellidos</th>
                  <th onClick={() => handleSort('concepto')}>Concepto</th>
                  <th onClick={() => handleSort('fechaDonacion')}>Fecha Ingreso</th>
                  <th onClick={() => handleSort('valor')}>Valor</th>
                  {canEditValues && <th>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filteredIngresos.map((ingreso) => (
                  <tr key={ingreso.id}>
                    <td data-label="Identificación">{ingreso.identificacion}</td>
                    <td data-label="Nombres">{ingreso.nombres}</td>
                    <td data-label="Apellidos">{ingreso.apellidos}</td>
                    <td data-label="Concepto">{ingreso.concepto}</td>
                    <td data-label="Fecha Donación">{new Date(ingreso.fechaDonacion).toLocaleDateString()}</td>
                    <td data-label="Valor">{formatCurrency(ingreso.valor)}</td>
                    {canEditValues && (
                      <td data-label="Acciones">
                        <button
                          onClick={() => handleEditClick(ingreso)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title="Editar valor"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

           <p>  </p>
            <button type="button" className="submit-back" onClick={handleGoBack}>
              Regresar al menú
            </button>
            <p>  </p>.    <p>  </p>.    <p>  </p>
          </div>
        </div>
      </div>

      {/* Modal de edición */}
      {showModal && editingRecord && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ marginTop: 0, color: '#333' }}>Editar Valor de Donación</h2>
            
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ margin: '0.5rem 0', color: '#666' }}>
                <strong>Donante:</strong> {editingRecord.nombres} {editingRecord.apellidos}
              </p>
              <p style={{ margin: '0.5rem 0', color: '#666' }}>
                <strong>Identificación:</strong> {editingRecord.identificacion}
              </p>
              <p style={{ margin: '0.5rem 0', color: '#666' }}>
                <strong>Concepto:</strong> {editingRecord.concepto}
              </p>
              <p style={{ margin: '0.5rem 0', color: '#666' }}>
                <strong>Valor actual:</strong> {formatCurrency(editingRecord.valor)}
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="editValueInput" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                Nuevo valor:
              </label>
              <input
                id="editValueInput"
                type="number"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #667eea',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelEdit}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                <i className="fas fa-save" style={{ marginRight: '0.5rem' }}></i>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReporteIngresos;
